import type { LoaderProps } from '@ui/components/v2/loader';
import { Loader } from '@ui/components/v2/loader';

import { wrapClassNames } from '@ui/utils/tailwind';
import type React from 'react';
import type { ComponentProps, PropsWithChildren } from 'react';
import { forwardRef } from 'react';

const buttonSizes = {
  '2xs': 'gap-0.5 px-1.5 h-5 typo-caption-1',
  xs: 'gap-1 px-2 h-6 typo-caption-1',
  sm: 'gap-1.5 px-3 h-8 typo-callout',
  md: 'gap-2 px-4 h-10 typo-callout',
  lg: 'gap-3 px-6 h-13 typo-headline',
  icon: {
    '2xs': 'w-5 h-5 typo-caption-1',
    xs: 'w-6 h-6 typo-caption-1',
    sm: 'w-8 h-8 typo-callout',
    md: 'w-10 h-10 typo-callout',
    lg: 'w-13 h-13 typo-headline',
  },
};

type ButtonSize = '2xs' | 'xs' | 'sm' | 'md' | 'lg';

const buttonShapes = {
  default: {
    '2xs': 'rounded-[0.25rem]',
    xs: 'rounded-md',
    sm: 'rounded-md',
    md: 'rounded-lg',
    lg: 'rounded-xl',
  },
  pill: 'rounded-full',
};

type ButtonShapes = keyof typeof buttonShapes;

export const buttonVariants = {
  danger:
    'bg-red-100 text-status-danger focus:ring-red-300 focus:bg-status-danger-bg-light hover:bg-status-danger-bg-light',
  default: 'bg-background-ui text-white focus:ring-blue-300 focus:bg-blue-700 hover:bg-blue-700',
  input: `
    bg-white text-typography border border-border
    focus:ring-1 focus:ring-opacity-100 focus:ring-offset-0 focus:border-blue-600 focus:ring-blue-600
    data-[state=open]:ring-1 data-[state=open]:ring-offset-0 data-[state=open]:ring-blue-600 data-[state=open]:border-blue-600
    hover:border-neutral-300
  `,
  link: 'bg-transparent text-neutral-800 focus:ring-neutral-200 focus:bg-neutral-100 hover:bg-neutral-100',
  secondary:
    'bg-neutral-100 text-neutral-800 focus:ring-neutral-300 focus:bg-background-tertiary hover:bg-background-tertiary',
  success: 'bg-emerald-600 text-white focus:ring-emerald-300 focus:bg-status-success hover:bg-status-success',
  learning: 'bg-learning text-learning-text focus:ring-learning-200 focus:bg-learning-600 hover:bg-learning-600',
  white: 'bg-white text-typography focus:ring-neutral-200 focus:bg-neutral-100 hover:bg-neutral-100',
  transparent: 'bg-transparent text-typography focus:ring-0',
  organization:
    'bg-organization-500 text-organization-text focus:ring-organization-200 focus:bg-organization-600 hover:bg-organization-600',
  dark: 'bg-slate-900 text-gray-200 focus:bg-slate-700 hover:bg-slate-700',
};

export type ButtonVariant = keyof typeof buttonVariants;

const buttonVariantsToLoaderVariants: Record<ButtonVariant, LoaderProps['variant']> = {
  danger: 'danger',
  default: 'white',
  input: 'default',
  link: 'default',
  secondary: 'default',
  success: 'white',
  learning: 'white',
  white: 'white',
  organization: 'white',
  dark: 'white',
  transparent: 'white',
};

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  href?: string;
  icon?: boolean;
  loader?: boolean;
  rel?: string;
  shape?: ButtonShapes;
  size?: ButtonSize;
  tagName?: string | React.ComponentType<any>;
  target?: string;
  variant?: ButtonVariant;
  tw?: never;
}

export const Button = forwardRef<HTMLButtonElement, PropsWithChildren<ButtonProps>>(
  (
    {
      children,
      disabled,
      icon,
      loader,
      shape = 'default',
      size = 'md',
      tagName: Tag = 'button',
      variant = 'default',
      className = '',
      ...rest
    },
    ref
  ) => {
    const isDisabled = disabled || loader;

    return (
      <Tag
        ref={ref}
        {...rest}
        className={wrapClassNames(`
          inline-flex justify-center items-center font-semibold text-center cursor-pointer
          transition duration-300 ease-in-out
          focus:ring-2 focus:ring-opacity-50 focus:ring-offset-1 focus:outline-none focus:z-10
          ${icon ? buttonSizes.icon[size] : buttonSizes[size]}
          ${shape === 'pill' ? buttonShapes.pill : buttonShapes[shape][size]}
          ${buttonVariants[variant]}
          ${isDisabled ? 'opacity-60 pointer-events-none' : ''}
          ${className}
        `)}
        disabled={isDisabled}
        aria-busy={loader || undefined}
      >
        {loader ? <Loader size="md" variant={buttonVariantsToLoaderVariants[variant]} /> : children}
      </Tag>
    );
  }
);

const buttonGroupJustificationStyles = {
  center: 'justify-center',
  end: 'justify-end',
  footer: 'justify-end [&>button:first-child]:mr-auto',
  start: 'justify-start',
  stretch: '[&>button]:flex-1',
};

type ButtonGroupJustification = keyof typeof buttonGroupJustificationStyles;

const buttonGroupOrientationStyles = {
  horizontal: 'flex-row space-x-3',
  vertical: 'flex-col space-y-2',
};

type ButtonGroupOrientation = keyof typeof buttonGroupOrientationStyles;

interface ButtonGroupProps {
  justification?: ButtonGroupJustification;
  orientation?: ButtonGroupOrientation;
  className?: string;
}

export const ButtonGroup = ({
  children,
  justification = 'stretch',
  orientation = 'horizontal',
  className = '',
  ...props
}: PropsWithChildren<ComponentProps<'div'> & ButtonGroupProps>) => (
  <div
    {...props}
    className={wrapClassNames(`flex
      ${orientation === 'horizontal' && buttonGroupJustificationStyles[justification]}
      ${buttonGroupOrientationStyles[orientation]}
      ${className}
  `)}
  >
    {children}
  </div>
);
